/* globals google */

/**
 * This is handler for Google maps directions API
 *
 * @author Tamara Bazko
 **/

import $ from 'jquery';
import Mustache from 'mustache';
import createLogger from 'components/logger/Logger';
import EventTypes from 'components/EventTypes';
import directionsStepsTemplate from './../templates/directionsSteps.html';

const Logger = createLogger('googleDirections');
const GoogleDirections = {
  /**
   **** PRIVATE PROPERTIES ****
   */
  _$directionsOverlay: undefined,
  _directionsDisplay: undefined,
  _directionsService: undefined,
  _initialized: undefined,
  _lineDestination: undefined,
  _lineOrigin: undefined,
  _mapCanvas: undefined,

  /******************* PUBLIC METHODS *******************
   *
   *
   * Gets origin (user location or entered value), destination
   * (always store address, user shouldn't be able to enter random address), travel mode
   * and sets route
   * @param    {Object/String}   origin           Can be LatLng or String with address
   * @param    {Object/String}   destination      Can be LatLng or String with address
   * @param    {String}          type             Name of travel mode
   */
  calculateAndDisplayRoute(origin, destination, type) {
    switch (type) {
      case 'driving':
        type = google.maps.TravelMode.DRIVING;
        break;
      case 'bicycling':
        type = google.maps.TravelMode.BICYCLING;
        break;
      case 'transit':
        type = google.maps.TravelMode.TRANSIT;
        break;
      case 'walking':
        type = google.maps.TravelMode.WALKING;
        break;
    }

    if (!this._directionsService) {
      this._directionsService = new google.maps.DirectionsService();
    }

    this._directionsService.route(
      {
        origin,
        destination,
        travelMode: type,
      },
      function (response, status) {
        if (status === google.maps.DirectionsStatus.OK) {
          this._directionsDisplay.setDirections(response);
          const leg = response.routes[0].legs[0];
          const { bounds } = response.routes[0];
          const eventData = {
            bounds,
          };

          const directionsStepsContent = Mustache.render(directionsStepsTemplate, leg);
          this._$directionsList.each(function () {
            $(this).empty();
            $(directionsStepsContent).appendTo($(this));
          });

          this._removeDottedPolyline();

          if (typeof origin !== 'string') {
            this._lineOrigin = this._setDottedPolyline(leg.start_location, origin);
          } else {
            eventData.originLatLng = leg.start_location;
          }

          this._lineDestination = this._setDottedPolyline(leg.end_location, destination);
          this._$directionsOverlay.trigger(EventTypes.DIRECTIONS_ROUTE_DISPLAYED, eventData);
        } else {
          if (status === 'ZERO_RESULTS' || status === 'NOT_FOUND') {
            this._$directionsOverlay.trigger(EventTypes.DIRECTIONS_ROUTE_NOT_FOUND);
          }
          Logger.info(`Directions request failed due to ${status}`);
        }
      }.bind(this)
    );
  },

  /*
   * Initialization of directions, happens when directions overlay is shown
   * @param    {Object}   settings     includes properties: map
   */
  initialize(settings) {
    if (!this._initialized) {
      this._mapCanvas = settings.map;
      this._$directionsOverlay = settings.directionsOverlay;
      this._searchBox = new google.maps.places.SearchBox(settings.inputElement);
      this._$inputElement = $(settings.inputElement);
      this._$directionsList = settings.directionsList;
      this._$storeFinder = settings.storeFinder;

      this._directionsService = new google.maps.DirectionsService();
      this._directionsDisplay = new google.maps.DirectionsRenderer({
        suppressMarkers: true,
        suppressBicyclingLayer: true,
        preserveViewport: true,
        polylineOptions: {
          strokeColor: '#303030',
          strokeOpacity: 0.7,
        },
      });

      this._bindEvents();
      this._initialized = true;
    }

    try {
      $('.pac-container').addClass('pac-container--directions');
    } catch (error) {
      Logger.warn(`${error}possible that class does not exist`);
    }

    this.showDirectionsDisplay();
  },

  /*
   * Remove route display (e.g. when returning to store details overlay)
   */
  hideDirectionsDisplay() {
    this._removeDottedPolyline();
    this._directionsDisplay.setMap(null);
    this._$directionsList.each(function () {
      $(this).empty();
    });
  },

  /*
   * Show route
   */
  showDirectionsDisplay() {
    this._directionsDisplay.setMap(this._mapCanvas);
  },

  /**
   ******************* PRIVATE METHODS *******************
   *
   * Bind events
   */
  _bindEvents() {
    document.jq.on(EventTypes.DIRECTIONS_HIDE_OVERLAY, function () {
      try {
        $('.pac-container').removeClass('pac-container--directions');
      } catch (error) {
        Logger.warn(`${error}possible that class does not exist`);
      }
    });

    google.maps.event.addListener(
      this._searchBox,
      'places_changed',
      this._onPlacesChanged.bind(this)
    );
  },

  /*
   * On place changed (when suggestion from suggestion's list is clicked)
   */
  _onPlacesChanged() {
    const places = this._searchBox.getPlaces();
    let place;

    if (places) {
      place = places[0];

      if (place && place.address_components) {
        this._$directionsOverlay.trigger(EventTypes.DIRECTIONS_PLACES_CHANGED);
      } else {
        this._selectFirstResult();
      }
    }
  },

  /*
   * Removing grey dots (aren't removed together with route line)
   */
  _removeDottedPolyline() {
    if (this._lineOrigin) {
      this._lineOrigin.setMap(null);
    }

    if (this._lineDestination) {
      this._lineDestination.setMap(null);
    }
  },

  /*
   * Selecting first result from suggested search, as it's usually more appropriate
   * then if sending address directly from input (on enter hit e.g.)
   */
  _selectFirstResult() {
    if (document.getElementsByClassName('pac-item').length !== 0) {
      this._$inputElement.focus();
      google.maps.event.trigger(this._$inputElement.get(0), 'keydown', { keyCode: 40 });
      google.maps.event.trigger(this._$inputElement.get(0), 'keydown', { keyCode: 13 });
    }
  },

  /*
   * Set polyline as grey dots. Used for distance difference between real origin/destination
   * and "leg" start/end returned in google response (e.g. covers distance from road to building)
   * @param   {Object}    start
   * @param   {Object}    end
   */
  _setDottedPolyline(start, end) {
    // Define a symbol using SVG path notation.
    const lineSymbol = {
      path: 'M 0.1,0 0,0',
      strokeOpacity: 0.5,
      strokeColor: '#666666',
      scale: 5,
    };

    const line = new google.maps.Polyline({
      path: [start, end],
      strokeOpacity: 0,
      icons: [
        {
          icon: lineSymbol,
          offset: '50%',
          repeat: '12px',
        },
      ],
      map: this._mapCanvas,
    });

    return line;
  },
};

export default GoogleDirections;
